import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PersonIcon from '@/assets/icons/person'
import { useActiveCurrency, useCurrencies } from '@/hooks'
import FloatingPanel from '@/components/ui/FloatingPanel'
import { EyeIcon, StatsIcon, Crevron } from '@/assets/icons'
import MarketStatsTooltipContent from './MarketStatsTooltipContent'
import { abbreviateAmount, formatNumber } from '@/util/localizeNumber'
import { selectActiveLanguage } from '@/store/app'
import { useSelector } from 'react-redux'
import { i18n } from '@/locale/i18n'
import DeferredNumberFlow from '@/components/ui/deferred-number-flow'
import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import dayjs from 'dayjs'
import questionService from '@/api/question'
import classNames from 'classnames'
import { isSportsEvent } from '@/features/market/utils/sportsEvent'
import { isCryptoPriceEvent } from '@/features/market/utils/cryptoEvent'
import { WAGER_STATUS } from '@/constants/marketBackendEnums'
import { ClockIcon } from '@/assets/icons/index'
import CommentsIcon from '@/assets/icons/comments'
import DropLineIcon from '@/assets/icons/dropLine'

const QuestionFooter = ({
  question,
  inMarketPage,
  hasBets = false,
  isBetListCollapsed = false,
  toggleBetListCollapse = () => {},
  onCollapseButtonHover = () => {},
  renderShareFollow,
  renderFollowMarket,
  toogleFollow,
  isMobileMarketPage = false,
  compact = false,
}) => {
  const locale = useSelector(selectActiveLanguage())
  const currencies = useCurrencies().currencies
  const activeCurrency = useActiveCurrency()
  const isRealMoney = activeCurrency && activeCurrency.id !== currencies.OOM.id
  const [isStatsTooltipOpen, setIsStatsTooltipOpen] = useState(false)
  const statsAnchorRef = useRef(null)
  const statsPanelRef = useRef(null)
  const [statsTooltipPosition, setStatsTooltipPosition] = useState(null)

  const TOOLTIP_GAP = 8
  const TOOLTIP_VIEWPORT_MARGIN = 16

  // Tooltip is portaled to <body> so its z-index competes at the document level
  // instead of being trapped inside this card's local stacking context — without
  // this, a later sibling card (e.g. its header) paints over the tooltip on scroll.
  // Position is tracked in fixed coordinates from the anchor's own rect, and flips
  // to the opposite side of the anchor when the preferred side doesn't fit the viewport.
  const updateStatsTooltipPosition = () => {
    if (!statsAnchorRef.current) return
    const rect = statsAnchorRef.current.getBoundingClientRect()
    // offsetWidth/Height rather than a client rect: the panel animates in with a
    // scale/translate, and a rect would measure the transformed, mid-animation box.
    const panelHeight = statsPanelRef.current?.offsetHeight || 0
    const panelWidth = statsPanelRef.current?.offsetWidth || 0

    // Default: above the anchor on cards, below the anchor on the market page.
    let placement = inMarketPage ? 'below' : 'above'
    if (placement === 'above' && rect.top - panelHeight - TOOLTIP_GAP < 0) {
      placement = 'below'
    } else if (placement === 'below' && rect.bottom + panelHeight + TOOLTIP_GAP > window.innerHeight) {
      placement = 'above'
    }

    // Keep the panel inside the viewport so its own padding stays visible.
    const maxLeft = window.innerWidth - panelWidth - TOOLTIP_VIEWPORT_MARGIN
    const left = Math.max(TOOLTIP_VIEWPORT_MARGIN, Math.min(rect.left, maxLeft))

    setStatsTooltipPosition({
      left,
      placement,
      top: placement === 'below' ? rect.bottom : rect.top,
    })
  }

  const toggleStatsTooltip = () => {
    updateStatsTooltipPosition()
    setIsStatsTooltipOpen(open => !open)
  }

  // Layout effect, not an effect: the panel is unmounted while closed, so the
  // measurement taken when opening it sees no panel and neither the flip nor the
  // viewport clamp can apply. Re-measuring before paint keeps the first frame from
  // landing off-screen and then jumping into place.
  useLayoutEffect(() => {
    if (!isStatsTooltipOpen) return

    // Re-measure once the panel has actually rendered (real size available).
    updateStatsTooltipPosition()

    window.addEventListener('scroll', updateStatsTooltipPosition, true)
    window.addEventListener('resize', updateStatsTooltipPosition)
    return () => {
      window.removeEventListener('scroll', updateStatsTooltipPosition, true)
      window.removeEventListener('resize', updateStatsTooltipPosition)
    }
  }, [isStatsTooltipOpen, inMarketPage])

  // The mobile row opens the panel on tap, so it also needs an explicit dismiss.
  useEffect(() => {
    if (!isMobileMarketPage || !isStatsTooltipOpen) return

    const handlePointerDown = event => {
      if (statsAnchorRef.current?.contains(event.target) || statsPanelRef.current?.contains(event.target)) return
      setIsStatsTooltipOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isMobileMarketPage, isStatsTooltipOpen])

  const volumeLabel = question => {
    if (!question) return null

    const rawValue = (isRealMoney ? question.volume_real_money : question.volume_play_money) || 0
    const { value, abbr } = abbreviateAmount(rawValue, inMarketPage)

    const options = {
      locales: locale,
      value,
      format: {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: abbr ? 1 : 0,
      },
      roundingMode: 'floor', // round up to the nearest integer
      prefix: isRealMoney ? '$ ' : '',
      suffix: abbr + (isRealMoney ? '' : ' ø'),
    }
    return <DeferredNumberFlow {...options} />
  }
  // Always shown, even when volume is 0/null, so the stats tooltip stays reachable.
  const renderVolume = question => {
    return (
      <div>
        <div className="question-card__volume-text">
          <StatsIcon fillcolor={'var(--color-Neutral-4)'} width="14px" />
          <span>{volumeLabel(question)}</span>
        </div>
      </div>
    )
  }

  const renderNumberOfBets = question => {
    return (
      <div>
        <div className="question-card__number-of-bets-text">
          <PersonIcon className="question-card__person-icon" fillcolor="currentColor" width="14px" />

          <span className="question-card__wager-count">{forecastersCount(question)}</span>
        </div>
      </div>
    )
  }

  const forecastersCount = question => {
    return <DeferredNumberFlow locales={locale} value={question.wagers_count_total || question.wagers_count_canonical} />
  }

  const formatDate = date => {
    const now = dayjs()
    const bedDate = dayjs(date)

    // Check if the input date is valid
    if (!bedDate.isValid()) {
      return 'Invalid date'
    }

    const diffInSeconds = bedDate.diff(now, 'second')

    const diffInMinutes = Math.floor(diffInSeconds / 60)
    const diffInHours = Math.floor(diffInMinutes / 60)

    // If date > 24 hours or If the date is in the past, return formatted date
    if (diffInHours > 24 || diffInSeconds < 0) {
      return bedDate.format('MMM D, YYYY')
    }

    // If date is between 1 and 24 hours, show time in hours
    if (diffInHours >= 1) {
      return i18n('question_card.date.bet_end_date_in_hours', { hours: diffInHours })
    }

    // If date is less than 1 hour but >= 1 minute, show time in minutes
    if (diffInMinutes >= 1) {
      return i18n('question_card.date.bet_end_date_in_minutes', { minutes: diffInMinutes })
    }

    // If date is less than 1 minute, show time in seconds
    const minutes = String(Math.floor(diffInSeconds / 60)).padStart(2, '0')
    const seconds = String(diffInSeconds % 60).padStart(2, '0')

    return i18n('question_card.date.bet_end_date_in_minutes_and_seconds', {
      minutes: minutes,
      seconds: seconds,
    })
  }

  const renderBetEnd = () => {
    return (
      <div className="question-footer__bet-end-date text-Neutral-4 w-fit flex items-center gap-2">
        <ClockIcon width="14px" height="14px" fill="currentColor" /> {formatDate(question.bet_end_date)}
      </div>
    )
  }

  // Sports events always show their kickoff elsewhere — the fixture block on the detail page,
  // SportEventQuestionHeader on feed cards — and crypto price events carry an EventCountdown
  // beside the title, so in both cases the footer countdown would be a second timer for the
  // same deadline.
  const shouldShowBetEnd =
    question.bet_end_date &&
    !questionService.isResolved(question) &&
    !questionService.isCancelled(question) &&
    !isCryptoPriceEvent(question) &&
    !isSportsEvent(question)
  const liquidityLabel = question => {
    if (!question) return null
    const rawValue = (isRealMoney ? question.liquidity_real_money : question.liquidity_play_money) || 0
    const { value, abbr } = abbreviateAmount(rawValue, inMarketPage)

    const options = {
      locales: locale,
      value,
      format: {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: abbr ? 1 : 0,
      },
      roundingMode: 'floor',
      prefix: isRealMoney ? '$ ' : '',
      suffix: abbr + (isRealMoney ? '' : ' ø'),
    }
    return <DeferredNumberFlow {...options} />
  }

  const renderLiquidity = question => {
    return (
      <div>
        <div className="question-liquidity-text flex items-center gap-1.5 text-Neutral-4">
          <DropLineIcon fillcolor={'var(--color-Neutral-4)'} width="14px" height="14px" />
          <span>{liquidityLabel(question)}</span>
        </div>
      </div>
    )
  }

  // Exposed directly in the footer on the market page, instead of only inside the stats tooltip.
  const renderCommentsCount = question => {
    return (
      <div>
        <div className="question-card__comments-text flex items-center gap-1">
          <CommentsIcon width="14" height="14" fill="currentColor" />
          <span>{question.comment_count || 0}</span>
        </div>
      </div>
    )
  }

  const betSummary = renderNumberOfBets(question)
  const volume = renderVolume(question)
  // Liquidity and comments are market-page only; the cards keep just traders and volume.
  // Sports events keep that pared-back pair everywhere — the row sits under the fixture
  // block, where a four-stat line would compete with the scoreboard.
  const showExtraStats = inMarketPage && !isSportsEvent(question)
  const liquidity = showExtraStats ? renderLiquidity(question) : null
  const commentsCount = showExtraStats ? renderCommentsCount(question) : null
  // const currencyInfos = renderCurrencyInfos(activeCurrency.id)

  const renderCollapseButton = () => {
    if (!hasBets || inMarketPage) return null

    const betCount = question.wagers.filter((wager) => wager.status === WAGER_STATUS.PURCHASE).length || 0

    if(betCount === 0) return null

    return (
      <button
        className="cursor-pointer flex gap-1 items-center px-3 py-2 rounded-full text-xs font-bold transition-all bg-surface-3 text-Neutral-4 hover:bg-Neutral-3"
        onClick={toggleBetListCollapse}
        onMouseEnter={onCollapseButtonHover}
        title={isBetListCollapsed ? i18n('question_card.show_bets') : i18n('question_card.hide_bets')}>
        <span className=" inline-flex items-center justify-center pl-2 h-5  bg-[var(--color-Primary-1)] text-[var(--color-Primary-5)] rounded-full  font-semibold">
          {formatNumber(betCount, locale, 0, 0)}
        </span>
        <span
          className={`inline-flex items-center justify-center w-5 h-5 transition-transform duration-200 ${
            isBetListCollapsed ? '' : 'transform rotate-180'
          }`}>
          <Crevron width="12px" height="12px" fillcolor="var(--color-Neutral-6)" />
        </span>
      </button>
    )
  }

  /* Null without a market, so the `isOpen` check below still decides something: the
     panel has nothing to say about a market that isn't there. */
  const statsTooltipContent = question ? <MarketStatsTooltipContent question={question} locale={locale} /> : null

  const renderStatsTooltip = () =>
    typeof document !== 'undefined' &&
    createPortal(
      <FloatingPanel
        ref={statsPanelRef}
        isOpen={isStatsTooltipOpen && !!statsTooltipContent}
        className="w-[380px] max-w-[calc(100vw-32px)] p-4"
        style={
          statsTooltipPosition && {
            position: 'fixed',
            zIndex: 9999,
            /* The panel is portaled to <body> but stays a React child of the anchor, so
               moving onto it never fires the anchor's mouseleave and the hover tooltip
               would stay open over the controls it covers, swallowing their clicks. It
               carries no interactive content, so it takes no pointer events — except on
               the mobile row, which opens it by tap and dismisses it by pointerdown. */
            pointerEvents: isMobileMarketPage ? 'auto' : 'none',
            left: statsTooltipPosition.left,
            top: statsTooltipPosition.placement === 'below' ? statsTooltipPosition.top + TOOLTIP_GAP : undefined,
            bottom:
              statsTooltipPosition.placement === 'above'
                ? window.innerHeight - statsTooltipPosition.top + TOOLTIP_GAP
                : undefined,
          }
        }>
        {statsTooltipContent}
      </FloatingPanel>,
      document.body
    )

  // Mobile market page: only traders, volume and liquidity stay on the row — the bet end
  // date sits under the title (see QuestionHeader) and comments live in the stats panel,
  // opened by tapping the row.
  if (isMobileMarketPage) {
    // Crypto price up/down pages keep the row to traders and volume, grouped left under the
    // title; liquidity stays reachable in the stats panel.
    const isCompactStatsRow = isCryptoPriceEvent(question)

    return (
      <div
        ref={statsAnchorRef}
        role="button"
        tabIndex={0}
        aria-expanded={isStatsTooltipOpen}
        className={classNames(
          'question-card__stats-row question-card__stats-row--mobile relative flex items-center w-full pb-6 text-sm cursor-pointer',
          isCompactStatsRow ? 'justify-start gap-6' : 'justify-between',
          compact && 'question-card__stats-row--compact'
        )}
        onClick={toggleStatsTooltip}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleStatsTooltip()
          }
        }}>
        <div className="flex items-center">{betSummary}</div>
        <div className="flex items-center">{volume}</div>
        {!isCompactStatsRow && liquidity && <div className="flex items-center">{liquidity}</div>}
        {renderStatsTooltip()}
      </div>
    )
  }

  // Default layout for other cases

  return (
    <>
      <div
        className={`question-card__general-info w-full ${
          compact ? 'question-card__general-info--compact' : ''
        }`}>
        {/* Stats and the bet end date share a single row so they line up as one group. */}
        <div
          ref={statsAnchorRef}
          className="question-card__stats-row relative flex items-center -translate-x-2 gap-4 text-sm question-card__number-of-bets question-card__number-of-bets--labels"
          onMouseEnter={() => {
            updateStatsTooltipPosition()
            setIsStatsTooltipOpen(true)
          }}
          onMouseLeave={() => setIsStatsTooltipOpen(false)}>
          {betSummary}
          {volume}
          {liquidity}
          {commentsCount}
          {inMarketPage && shouldShowBetEnd && renderBetEnd()}
          {renderStatsTooltip()}
        </div>

        <div className="flex items-center gap-2">
          {inMarketPage && renderShareFollow && renderShareFollow()}
          {!inMarketPage && renderFollowMarket && <div>{renderFollowMarket()}</div>}
          {renderCollapseButton()}
        </div>
      </div>
    </>
  )
}

export default QuestionFooter
